import { SMap, voidFunction } from '@avantia/client-and-server-utilities';
import { History } from 'history';
import { HpClientDebugFlags } from '../clientDebugFlags';
import { logInfo } from '../clientLogging';
import { clearLocationTransition } from '../handlers/pageLoadDataHandler';
import { updateDocumentTitle } from '../tools/formatting';

export type HpHistoryWithState = History<HpPageLocationState>;

export interface HpPageLocationState {
  documentTitle: string;
  from: string;
  to: string;
}

export interface HpHistoryState<StateT extends HpPageLocationState> {
  key: string;
  state: StateT;
}

export interface HpPushToHistoryProps {
  documentTitle: string;
  history: HpHistoryWithState;
  keyValuePairs?: SMap<string>;
  nextPath: string;
  replace?: boolean;
}

let pushToHistoryHook: HpPushToHistoryHookFunction = voidFunction;

export function pushToHistory({
  documentTitle,
  history,
  keyValuePairs,
  nextPath,
  replace
}: HpPushToHistoryProps): void {
  const { location } = history;
  const { hash, pathname, search } = location;

  if (nextPath && nextPath !== '' && (nextPath !== pathname || search || hash)) {
    documentTitle = documentTitle || '';
    const state: HpPageLocationState = {
      from: `${pathname}${search}${hash}`,
      to: nextPath,
      documentTitle,
      ...(keyValuePairs || {})
    };
    logInfo(
      `history.${replace ? 'replace' : 'push'}(${JSON.stringify({ nextPath, state }).replaceAll('"', "'")})`,
      HpClientDebugFlags.PageLoadDataHandler
    );

    clearLocationTransition();

    // This has to be done before we push otherwise the text in the history will be (more) incorrect.
    updateDocumentTitle({ name: documentTitle });

    if (replace) {
      (state as any).actualTitle = document.title;
      history.replace(nextPath, state);
    } else {
      history.push(nextPath, state);
    }

    pushToHistoryHook({ nextPath, state, replace });
  }
}

export interface HpPushToHistoryHookProps {
  nextPath: string;
  replace?: boolean;
  state: HpPageLocationState;
}

export type HpPushToHistoryHookFunction = (props: HpPushToHistoryHookProps) => void;

// Used to store action history for back/forward navivation on the quote journey.
export function registerPushToHistoryHook(hook: HpPushToHistoryHookFunction): void {
  if (hook) {
    pushToHistoryHook = hook;
  }
}
